import { ethers } from 'ethers';
import { contractABI, contractAddress, bscTestnetRPC } from '../contracts/contractABI';

// Create a provider for read-only operations
export const getReadOnlyProvider = () => {
  try {
    return new ethers.JsonRpcProvider(bscTestnetRPC);
  } catch (error) {
    console.error('Error creating provider:', error);
    return null;
  }
};

// Create a contract instance for read-only operations
export const getReadOnlyContract = () => {
  try {
    const provider = getReadOnlyProvider();
    if (!provider) return null;
    return new ethers.Contract(contractAddress, contractABI, provider);
  } catch (error) {
    console.error('Error creating read-only contract:', error);
    return null;
  }
};

// Create a contract instance with signer for write operations
export const getSignerContract = async (library, account) => {
  if (!library || !account) return null;
  
  try {
    // In ethers v6, we need to get the signer differently
    const signer = await library.getSigner(account);
    return new ethers.Contract(contractAddress, contractABI, signer);
  } catch (error) {
    console.error('Error getting signer contract:', error);
    return null;
  }
};

// Contract state mapping
export const contractStateMap = {
  0: 'PENDING',
  1: 'PRICE_SET',
  2: 'ACTIVE',
  3: 'VOIDED',
  4: 'CLOSED'
};

// Read contract functions
export const getContractState = async () => {
  const contract = getReadOnlyContract();
  if (!contract) {
    return { stateNumber: 1, stateName: 'PRICE_SET' }; // Default for demo
  }
  
  try {
    const state = await contract.state();
    return {
      stateNumber: state,
      stateName: contractStateMap[state] || 'UNKNOWN'
    };
  } catch (error) {
    console.error('Error getting contract state:', error);
    return { stateNumber: 1, stateName: 'PRICE_SET' }; // Default for demo
  }
};

export const getTokenAddresses = async () => {
  const contract = getReadOnlyContract();
  if (!contract) {
    // Return demo values
    return { 
      nilaToken: '0xA31fb7667F80306690F5DF0d9A6ea272aBF97926', 
      usdcToken: '0xaC0ca2548e8131D20cBa89CCe33Ec7C74272F61E' 
    };
  }
  
  try {
    const [nilaToken, usdcToken] = await Promise.all([
      contract.nilaToken(),
      contract.usdcToken()
    ]);
    return { nilaToken, usdcToken };
  } catch (error) {
    console.error('Error getting token addresses:', error);
    // Return demo values
    return { 
      nilaToken: '0xA31fb7667F80306690F5DF0d9A6ea272aBF97926', 
      usdcToken: '0xaC0ca2548e8131D20cBa89CCe33Ec7C74272F61E' 
    };
  }
};

export const getPartyAddresses = async () => {
  const contract = getReadOnlyContract();
  if (!contract) {
    // Return demo values
    return { 
      partyN: '0xe388A99F42786D427efaD72e9da43693435f52BE', 
      partyB: '0x2974C4dBE0750c9988006fafD0a93753cb7d50c1', 
      partyE: '0x35dD39DEbf8e16b808826BE3FAcE26Ab1e325035' 
    };
  }
  
  try {
    const [partyN, partyB, partyE] = await Promise.all([
      contract.partyN(),
      contract.partyB(),
      contract.partyE()
    ]);
    return { partyN, partyB, partyE };
  } catch (error) {
    console.error('Error getting party addresses:', error);
    // Return demo values
    return { 
      partyN: '0xe388A99F42786D427efaD72e9da43693435f52BE', 
      partyB: '0x2974C4dBE0750c9988006fafD0a93753cb7d50c1', 
      partyE: '0x35dD39DEbf8e16b808826BE3FAcE26Ab1e325035' 
    };
  }
};

export const getAgreedNilaPrice = async () => {
  const contract = getReadOnlyContract();
  if (!contract) {
    return '0.08'; // Demo value
  }
  
  try {
    const price = await contract.agreedNilaPrice();
    return ethers.formatUnits(price, 18); // Assuming 18 decimals
  } catch (error) {
    console.error('Error getting agreed NILA price:', error);
    return '0.08'; // Demo value
  }
};

export const getCurrentNilaPrice = async () => {
  const contract = getReadOnlyContract();
  if (!contract) {
    return '0.075'; // Demo value
  }
  
  try {
    const price = await contract.getCurrentNilaPrice();
    return ethers.formatUnits(price, 18); // Assuming 18 decimals
  } catch (error) {
    console.error('Error getting current NILA price:', error);
    return '0.075'; // Demo value
  }
};

export const getLoanAmount = async () => {
  const contract = getReadOnlyContract();
  if (!contract) {
    return '1600000'; // Demo value
  }
  
  try {
    const amount = await contract.loanAmount();
    return ethers.formatUnits(amount, 6); // USDC has 6 decimals
  } catch (error) {
    console.error('Error getting loan amount:', error);
    return '1600000'; // Demo value
  }
};

export const getLoanDetails = async () => {
  const contract = getReadOnlyContract();
  if (!contract) {
    // Return demo values
    return {
      principal: '1600000',
      nextInterestDue: Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60, // 30 days from now
      daysRemaining: 180,
      currentCollateralRatio: 165
    };
  }
  
  try {
    const [principal, nextInterestDue, daysRemaining, currentCollateralRatio] = await contract.getLoanDetails();
    return {
      principal: ethers.formatUnits(principal, 6), // USDC has 6 decimals
      nextInterestDue: Number(nextInterestDue),
      daysRemaining: Number(daysRemaining),
      currentCollateralRatio: Number(currentCollateralRatio)
    };
  } catch (error) {
    console.error('Error getting loan details:', error);
    // Return demo values
    return {
      principal: '1600000',
      nextInterestDue: Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60, // 30 days from now
      daysRemaining: 180,
      currentCollateralRatio: 165
    };
  }
};

export const getContractDates = async () => {
  const contract = getReadOnlyContract();
  if (!contract) {
    const now = Math.floor(Date.now() / 1000);
    // Return demo values
    return {
      executionDate: now - 180 * 24 * 60 * 60, // 180 days ago
      lockEndDate: now + 180 * 24 * 60 * 60, // 180 days from now
      loanDueDate: now + 180 * 24 * 60 * 60, // 180 days from now
      lastInterestPayment: now - 30 * 24 * 60 * 60 // 30 days ago
    };
  }
  
  try {
    const [executionDate, lockEndDate, loanDueDate, lastInterestPayment] = await Promise.all([
      contract.executionDate(),
      contract.lockEndDate(),
      contract.loanDueDate(),
      contract.lastInterestPayment()
    ]);
    
    return {
      executionDate: Number(executionDate),
      lockEndDate: Number(lockEndDate),
      loanDueDate: Number(loanDueDate),
      lastInterestPayment: Number(lastInterestPayment)
    };
  } catch (error) {
    console.error('Error getting contract dates:', error);
    const now = Math.floor(Date.now() / 1000);
    // Return demo values
    return {
      executionDate: now - 180 * 24 * 60 * 60, // 180 days ago
      lockEndDate: now + 180 * 24 * 60 * 60, // 180 days from now
      loanDueDate: now + 180 * 24 * 60 * 60, // 180 days from now
      lastInterestPayment: now - 30 * 24 * 60 * 60 // 30 days ago
    };
  }
};

export const checkCollateral = async () => {
  const contract = getReadOnlyContract();
  if (!contract) {
    return '0'; // Demo value
  }
  
  try {
    const requiredTopUp = await contract.checkCollateral();
    return ethers.formatUnits(requiredTopUp, 18); // Assuming 18 decimals for NILA
  } catch (error) {
    console.error('Error checking collateral:', error);
    return '0'; // Demo value
  }
};

export const checkLateInterest = async () => {
  const contract = getReadOnlyContract();
  if (!contract) {
    return false; // Demo value
  }
  
  try {
    return await contract.checkLateInterest();
  } catch (error) {
    console.error('Error checking late interest:', error);
    return false; // Demo value
  }
};

// Write contract functions
export const submitPriceB = async (library, account, price) => {
  if (!library || !account || !price) {
    console.error('Missing parameters for submitPriceB');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const priceInWei = ethers.parseUnits(price, 18); // Assuming 18 decimals
    const tx = await contract.submitPriceB(priceInWei);
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error submitting price B:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const submitPriceE = async (library, account, price) => {
  if (!library || !account || !price) {
    console.error('Missing parameters for submitPriceE');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const priceInWei = ethers.parseUnits(price, 18); // Assuming 18 decimals
    const tx = await contract.submitPriceE(priceInWei);
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error submitting price E:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const depositN = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for depositN');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.depositN();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error depositing N:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const depositB = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for depositB');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.depositB();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error depositing B:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const depositE = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for depositE');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.depositE();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error depositing E:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const signByB = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for signByB');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.signByB();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error signing by B:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const signByE = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for signByE');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.signByE();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error signing by E:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const payInterest = async (library, account, amount) => {
  if (!library || !account || !amount) {
    console.error('Missing parameters for payInterest');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const amountInWei = ethers.parseUnits(amount, 6); // USDC has 6 decimals
    const tx = await contract.payInterest(amountInWei);
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error paying interest:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const withdrawInterest = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for withdrawInterest');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.withdrawInterest();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error withdrawing interest:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const requestCollateralTopUp = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for requestCollateralTopUp');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.requestCollateralTopUp();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error requesting collateral top-up:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const topUpCollateral = async (library, account, amount) => {
  if (!library || !account || !amount) {
    console.error('Missing parameters for topUpCollateral');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const amountInWei = ethers.parseUnits(amount, 18); // Assuming 18 decimals for NILA
    const tx = await contract.topUpCollateral(amountInWei);
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error topping up collateral:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const applyLateInterestPenalty = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for applyLateInterestPenalty');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.applyLateInterestPenalty();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error applying late interest penalty:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const voidContract = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for voidContract');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.voidContract();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error voiding contract:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const withdrawAfterVoid = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for withdrawAfterVoid');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.withdrawAfterVoid();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error withdrawing after void:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const repayLoan = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for repayLoan');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.repayLoan();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error repaying loan:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

export const withdrawAfterClose = async (library, account) => {
  if (!library || !account) {
    console.error('Missing parameters for withdrawAfterClose');
    return { success: false, error: 'Missing parameters' };
  }
  
  try {
    const contract = await getSignerContract(library, account);
    if (!contract) {
      return { success: false, error: 'Contract not available' };
    }
    
    const tx = await contract.withdrawAfterClose();
    const receipt = await tx.wait();
    return { success: true, hash: receipt.hash };
  } catch (error) {
    console.error('Error withdrawing after close:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
};

// Format token amount with proper decimals
export const formatTokenAmount = (amount, decimals = 18) => {
  if (!amount) return '0';
  
  try {
    return ethers.formatUnits(amount, decimals);
  } catch (error) {
    console.error('Error formatting token amount:', error);
    return '0';
  }
};

// Parse token amount to wei
export const parseTokenAmount = (amount, decimals = 18) => {
  if (!amount) return '0';
  
  try {
    return ethers.parseUnits(amount, decimals);
  } catch (error) {
    console.error('Error parsing token amount:', error);
    return '0';
  }
};

// Format timestamp to date string
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'N/A';
  }
};
